import { useEffect, useRef, useState } from 'react'
import { Camera, Trash2, Upload, X } from 'lucide-react'
import { deletePatientPhoto, uploadPatientPhoto } from '../../api/patients'
import { Button } from '../../components/ui/Button'
import { useToast } from '../../components/ui/Toast'
import { cn } from '@/lib/utils'
import type { Patient } from '../../types/patients'

interface Props {
  patient: Patient
  onClose: () => void
  onUpdated: (patient: Patient) => void
}

type Tab = 'upload' | 'webcam'

export default function PatientPhotoModal({ patient, onClose, onUpdated }: Props) {
  const toast = useToast()
  const [activeTab, setActiveTab] = useState<Tab>('upload')
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Webcam state
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [stream, setStream] = useState<MediaStream | null>(null)
  const [captured, setCaptured] = useState(false)
  const [camError, setCamError] = useState<string | null>(null)
  const [camSupported] = useState(() => !!navigator.mediaDevices?.getUserMedia)

  // Clean up preview object URL on unmount or file change
  useEffect(() => {
    return () => { if (preview?.startsWith('blob:')) URL.revokeObjectURL(preview) }
  }, [preview])

  // Stop stream on unmount
  useEffect(() => {
    return () => stopStream()
  }, [])

  const stopStream = () => {
    setStream(s => {
      s?.getTracks().forEach(t => t.stop())
      return null
    })
    if (videoRef.current) videoRef.current.srcObject = null
  }

  const startWebcam = async () => {
    setCamError(null)
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
      setStream(s)
      if (videoRef.current) {
        videoRef.current.srcObject = s
        await videoRef.current.play()
      }
    } catch {
      setCamError('Could not access camera. Check your browser permissions.')
    }
  }

  const handleTabChange = (tab: Tab) => {
    if (tab === activeTab) return
    setActiveTab(tab)
    setFile(null)
    setPreview(null)
    setError(null)
    setCaptured(false)
    if (tab === 'webcam') {
      void startWebcam()
    } else {
      stopStream()
    }
  }

  const acceptFile = (f: File) => {
    if (!f.type.match(/^image\/(jpeg|png|webp)$/)) {
      setError('Only JPEG, PNG, and WebP images are accepted.')
      return
    }
    if (f.size > 8 * 1024 * 1024) {
      setError('Photo must be under 8 MB.')
      return
    }
    setError(null)
    setFile(f)
    setPreview(URL.createObjectURL(f))
  }

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (f) acceptFile(f)
    e.target.value = ''
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragging(false)
    const f = e.dataTransfer.files?.[0]
    if (f) acceptFile(f)
  }

  const takePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return
    const video = videoRef.current
    const canvas = canvasRef.current
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    canvas.getContext('2d')!.drawImage(video, 0, 0)
    canvas.toBlob(blob => {
      if (!blob) return
      const captured = new File([blob], 'webcam-photo.jpg', { type: 'image/jpeg' })
      stopStream()
      setCaptured(true)
      setFile(captured)
      setPreview(URL.createObjectURL(blob))
    }, 'image/jpeg', 0.92)
  }

  const retake = () => {
    setFile(null)
    setPreview(null)
    setCaptured(false)
    void startWebcam()
  }

  const handleUpload = async () => {
    if (!file || uploading) return
    setUploading(true)
    setError(null)
    try {
      const updated = await uploadPatientPhoto(patient.id, file)
      toast.success('Photo updated.')
      onUpdated(updated)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed.')
    } finally {
      setUploading(false)
    }
  }

  const handleRemove = async () => {
    setRemoving(true)
    try {
      const updated = await deletePatientPhoto(patient.id)
      toast.success('Photo removed.')
      onUpdated(updated)
    } catch {
      toast.error('Failed to remove photo.')
    } finally {
      setRemoving(false)
    }
  }

  const tabClass = (tab: Tab) => cn(
    'px-4 py-2 text-sm font-semibold border-b-2 transition-colors cursor-pointer bg-transparent border-l-0 border-r-0 border-t-0',
    activeTab === tab
      ? 'border-accent text-accent'
      : 'border-transparent text-text-3 hover:text-text-2',
  )

  return (
    <div
      className="fixed inset-0 z-[10000] bg-[rgba(10,25,22,0.52)] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Patient Photo"
        className="bg-surface rounded-[14px] w-full max-w-[460px] shadow-[0_12px_48px_rgba(0,0,0,0.24)] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <h2 className="m-0 text-[1.05rem] font-bold text-text-heading">Patient Photo</h2>
          <button
            onClick={onClose}
            className="bg-transparent border-0 cursor-pointer text-text-3 p-1 rounded-[6px] hover:bg-surface-hover transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border px-6 shrink-0">
          <button className={tabClass('upload')} onClick={() => handleTabChange('upload')}>
            <Upload size={13} className="inline mr-1.5 -mt-0.5" />
            Upload File
          </button>
          {camSupported && (
            <button className={tabClass('webcam')} onClick={() => handleTabChange('webcam')}>
              <Camera size={13} className="inline mr-1.5 -mt-0.5" />
              Webcam
            </button>
          )}
        </div>

        {/* Body */}
        <div className="px-6 py-5">

          {activeTab === 'upload' && (
            <>
              {preview ? (
                <div className="relative rounded-lg overflow-hidden bg-surface-alt">
                  <img src={preview} alt="Preview" className="w-full max-h-64 object-contain" />
                  <button
                    type="button"
                    onClick={() => { setFile(null); setPreview(null) }}
                    className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 transition-colors border-0 cursor-pointer"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <label
                  className={cn(
                    'flex flex-col items-center justify-center gap-3 h-48 rounded-lg border-2 border-dashed cursor-pointer transition-colors',
                    dragging ? 'border-accent bg-accent/5' : 'border-border-input bg-surface-alt hover:border-accent hover:bg-accent/5',
                  )}
                  onDragOver={e => { e.preventDefault(); setDragging(true) }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={handleDrop}
                >
                  <Camera size={32} strokeWidth={1.5} className="text-text-4" />
                  <div className="text-center">
                    <div className="text-sm font-semibold text-text-2">Click to browse or drag &amp; drop</div>
                    <div className="text-xs text-text-4 mt-1">JPEG, PNG, WebP · Max 8 MB</div>
                  </div>
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleFileInput} />
                </label>
              )}
            </>
          )}

          {activeTab === 'webcam' && (
            <div className="flex flex-col items-center gap-3">
              {camError ? (
                <div className="w-full h-48 rounded-lg bg-surface-alt flex items-center justify-center text-center px-6">
                  <p className="text-sm text-text-3">{camError}</p>
                </div>
              ) : captured && preview ? (
                <div className="relative w-full rounded-lg overflow-hidden bg-black">
                  <img src={preview} alt="Captured" className="w-full max-h-64 object-contain" />
                </div>
              ) : (
                <div className="relative w-full rounded-lg overflow-hidden bg-black">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full max-h-64 object-contain"
                  />
                </div>
              )}
              <canvas ref={canvasRef} className="hidden" />

              {!camError && (
                captured ? (
                  <Button type="button" variant="outline" size="sm" onClick={retake}>
                    Retake
                  </Button>
                ) : (
                  <Button type="button" size="sm" onClick={takePhoto} disabled={!stream}>
                    <Camera size={14} className="mr-1.5" />
                    Take Photo
                  </Button>
                )
              )}
            </div>
          )}

          {error && <p className="mt-3 text-sm text-danger-text">{error}</p>}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-2 px-6 py-4 border-t border-border shrink-0">
          <Button
            onClick={() => void handleUpload()}
            disabled={!file || uploading}
          >
            {uploading ? 'Uploading…' : 'Upload Photo'}
          </Button>
          <Button type="button" variant="outline" onClick={onClose} disabled={uploading}>
            Cancel
          </Button>
          {patient.photo_url && (
            <Button
              type="button"
              variant="danger"
              className="ml-auto"
              onClick={() => void handleRemove()}
              disabled={removing}
            >
              <Trash2 size={14} className="mr-1.5" />
              {removing ? 'Removing…' : 'Remove Photo'}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
